import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save, Sparkles, KeyRound, Bot, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Catálogo de modelos (mismo que módulo Automatización · Emails Procuradores)
const MODELOS_POR_PROVEEDOR = {
  openai: [
    { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra · recomendado' },
    { value: 'gpt-6-luna',    label: 'GPT-6 Luna · máxima calidad · coste alto' },
    { value: 'gpt-5.5',       label: 'GPT-5.5' },
    { value: 'gpt-5.4',       label: 'GPT-5.4' },
    { value: 'gpt-5.4-mini',  label: 'GPT-5.4-mini · rápido y económico' },
    { value: 'gpt-5.2',       label: 'GPT-5.2' },
    { value: 'o3',            label: 'o3 · razonamiento · coste alto' },
  ],
  anthropic: [
    { value: 'claude-sonnet-5-5',          label: 'Claude Sonnet 5.5 · recomendado' },
    { value: 'claude-sonnet-5',            label: 'Claude Sonnet 5' },
    { value: 'claude-opus-5-5',            label: 'Claude Opus 5.5 · máxima calidad · coste alto' },
    { value: 'claude-opus-5',              label: 'Claude Opus 5 · coste alto' },
    { value: 'claude-opus-4-8',            label: 'Claude Opus 4.8 · coste alto' },
    { value: 'claude-opus-4-7',            label: 'Claude Opus 4.7 · coste alto' },
    { value: 'claude-sonnet-4-6',          label: 'Claude Sonnet 4.6' },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-opus-4-5-20251101',   label: 'Claude Opus 4.5 · coste alto' },
    { value: 'claude-haiku-4-5-20251001',  label: 'Claude Haiku 4.5 · rápido y económico' },
  ],
  gemini: [
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro · recomendado · gran contexto' },
    { value: 'gemini-3.8-flash',       label: 'Gemini 3.8 Flash · rápido' },
    { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (preview)' },
    { value: 'gemini-2.5-pro',         label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.5-flash',       label: 'Gemini 2.5 Flash · rápido y económico' },
  ],
  qwen: [
    { value: 'qwen-max',   label: 'Qwen Max' },
    { value: 'qwen-plus',  label: 'Qwen Plus' },
    { value: 'qwen-turbo', label: 'Qwen Turbo · económico' },
  ],
  deepseek: [
    { value: 'deepseek-chat',     label: 'DeepSeek Chat' },
    { value: 'deepseek-reasoner', label: 'DeepSeek Reasoner' },
  ],
  otro: [],
};

const PROVEEDORES_EMERGENT = ['openai', 'anthropic', 'gemini'];

const AIExpedienteSettings = () => {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [testing, setTesting]   = useState(false);
  const [proveedor, setProveedor] = useState('openai');
  const [modelo,    setModelo]    = useState('gpt-5.6-terra');
  const [apiKey,    setApiKey]    = useState('');
  const [tieneApiKey, setTieneApiKey] = useState(false);
  const [testResult,  setTestResult]  = useState(null);

  const esExterno = !PROVEEDORES_EMERGENT.includes(proveedor);
  const opcionesModelo = MODELOS_POR_PROVEEDOR[proveedor] || [];

  const headers = () => {
    const tok = obtenerToken();
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ai-settings/expedientes`, { headers: headers() });
      const d = r.data || {};
      // Soporta esquema nuevo (proveedor/modelo) y viejo (provider/geminiApiKey)
      let prov = d.proveedor;
      if (!prov) {
        prov = d.provider === 'chatgpt' ? 'openai'
             : d.provider === 'gemini'  ? 'gemini'
             : d.provider === 'deepseek'? 'deepseek'
             : 'openai';
      }
      setProveedor(prov);
      const defModel = (MODELOS_POR_PROVEEDOR[prov] && MODELOS_POR_PROVEEDOR[prov][0]?.value) || 'gpt-5.5';
      setModelo(d.modelo || defModel);
      setTieneApiKey(!!d.tiene_api_key_custom);
    } catch (e) {
      console.error('Error cargando config IA Expedientes:', e);
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const onProveedor = (v) => {
    setProveedor(v);
    const opts = MODELOS_POR_PROVEEDOR[v] || [];
    if (opts.length > 0 && !opts.some(o => o.value === modelo)) {
      setModelo(opts[0].value);
    } else if (opts.length === 0) {
      setModelo('');
    }
  };

  const guardar = async () => {
    if (!modelo.trim()) {
      toast({ title: 'Falta modelo', description: 'Selecciona o escribe un modelo.', variant: 'destructive' });
      return;
    }
    if (esExterno && !tieneApiKey && !apiKey.trim()) {
      toast({ title: 'Falta API Key', description: `El proveedor "${proveedor}" requiere API key propia.`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = { proveedor, modelo: modelo.trim() };
      if (apiKey.trim()) body.api_key_custom = apiKey.trim();
      await axios.put(`${BACKEND_URL}/api/ai-settings/expedientes`, body, { headers: headers() });
      toast({ title: 'Configuración guardada', description: `Ahora: ${proveedor} · ${modelo}` });
      setApiKey('');
      await cargar();
    } catch (e) {
      toast({ title: 'Error guardando', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const probar = async () => {
    setTesting(true); setTestResult(null);
    try {
      const body = { proveedor, modelo: modelo.trim() };
      if (apiKey.trim()) body.api_key_custom = apiKey.trim();
      const r = await axios.post(`${BACKEND_URL}/api/ai-settings/expedientes/test`, body, { headers: headers() });
      const txt = (r.data?.response || r.data?.message || 'OK');
      setTestResult({ success: true, message: String(txt).substring(0, 400) });
      toast({ title: 'Test exitoso', description: 'La IA respondió correctamente.' });
    } catch (e) {
      setTestResult({ success: false, message: e?.response?.data?.detail || e.message });
      toast({ title: 'Error en test', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTesting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-8">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ai-expediente-settings">
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">IA para Análisis de Expedientes</h3>
              <p className="text-sm text-blue-700 mt-1">
                Configura el modelo IA utilizado en el análisis automático de expedientes:
              </p>
              <ul className="text-sm text-blue-600 mt-2 space-y-1">
                <li>• Análisis del iter procesal (botón "Analizar IA")</li>
                <li>• Resúmenes de actuaciones</li>
                <li>• Búsqueda y chat sobre actuaciones</li>
                <li>• Estrategia y probabilidad de éxito</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" /> Proveedor y Modelo
          </CardTitle>
          <CardDescription>
            Para OpenAI / Anthropic / Gemini usa la Emergent LLM Key del servidor.
            Para Qwen / DeepSeek / Otro debes aportar API key propia.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Proveedor</Label>
            <Select value={proveedor} onValueChange={onProveedor}>
              <SelectTrigger data-testid="ai-exp-proveedor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI (Emergent)</SelectItem>
                <SelectItem value="anthropic">Anthropic / Claude (Emergent)</SelectItem>
                <SelectItem value="gemini">Google Gemini (Emergent)</SelectItem>
                <SelectItem value="qwen">Qwen (API propia)</SelectItem>
                <SelectItem value="deepseek">DeepSeek (API propia)</SelectItem>
                <SelectItem value="otro">Otro (OpenAI-compatible, API propia)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Modelo</Label>
            {opcionesModelo.length > 0 ? (
              <Select value={modelo} onValueChange={setModelo}>
                <SelectTrigger data-testid="ai-exp-modelo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opcionesModelo.map(o =>
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            ) : (
              <Input value={modelo} onChange={(e) => setModelo(e.target.value)}
                     placeholder="nombre-del-modelo"
                     data-testid="ai-exp-modelo-input" />
            )}
            <div className="text-xs text-gray-500 mt-1">
              Valor técnico actual: <code className="bg-gray-100 px-1">{proveedor} / {modelo}</code>
            </div>
          </div>

          {esExterno && (
            <div>
              <Label className="text-xs flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> Custom API Key
                {tieneApiKey && <span className="text-green-700 ml-2">✓ ya guardada</span>}
              </Label>
              <Input type="password" value={apiKey}
                     onChange={(e) => setApiKey(e.target.value)}
                     placeholder={tieneApiKey ? 'Dejar vacío para conservar la actual' : 'sk-...'}
                     data-testid="ai-exp-apikey" />
              <div className="text-xs text-gray-500 mt-1">
                Se almacena cifrada en `configuracion_empresa.ai_expedientes_api_key`.
              </div>
            </div>
          )}

          <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
            ℹ️ Modelos avanzados (GPT-5.2, Claude Opus 4.5, Gemini 3 Pro) son más precisos pero consumen más saldo de Emergent Universal Key.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Button variant="outline" onClick={probar} disabled={testing}
                    data-testid="ai-exp-test" className="flex-1">
              {testing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Probando...</>
                : <><Bot className="w-4 h-4 mr-2" />Probar Conexión</>}
            </Button>
            <Button onClick={guardar} disabled={saving}
                    data-testid="ai-exp-save"
                    className="flex-1 bg-blue-600 hover:bg-blue-700">
              {saving
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                : <><Save className="w-4 h-4 mr-2" />Guardar Configuración</>}
            </Button>
          </div>
          {testResult && (
            <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-2">
                {testResult.success
                  ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  : <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />}
                <div className="text-sm">{testResult.message}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIExpedienteSettings;
